import { useState, useEffect, useRef } from 'react';
import {
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconVolume,
  IconVolumeOff,
  IconLoader2,
  IconMicrophoneOff,
} from '@tabler/icons-react';
import { attachAudioEvents } from '@common/utils';
import { AudioPlayerProps } from '../../../types';

function AudioPlayer({ url, isUserMessage, isSilent }: AudioPlayerProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);

  const audioRef = useRef<HTMLAudioElement>(null);
  const timelineRef = useRef<HTMLInputElement>(null);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || (audio && audio.readyState < 2)) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((err) => {
        console.error('Error playing audio:', err);
        setIsPlaying(false);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleToggleMute = () => {
    const audio = audioRef.current;
    if (audio) {
      audio.muted = !audio.muted;
      setIsMuted(audio.muted);
    }
  };

  const updateTimelineProgress = () => {
    const audio = audioRef.current;
    const timeline = timelineRef.current;
    if (audio && timeline) {
      const percentagePosition = (100 * audio.currentTime) / audio.duration;
      timeline.style.backgroundSize = `${percentagePosition}% 100%`;
      timeline.value = percentagePosition.toString();
      setCurrentTime(audio.currentTime);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleSeekChange = () => {
    const audio = audioRef.current;
    const timeline = timelineRef.current;
    if (audio && timeline) {
      const time = (Number(timeline.value) * audio.duration) / 100;
      if (isFinite(time)) {
        audio.currentTime = time;
        setCurrentTime(time);
      }
    }
  };

  useEffect(() => {
    const audio = audioRef.current;
    const timeline = timelineRef.current;

    if (audio && timeline) {
      // Add event listeners for audio loading states
      const handleCanPlay = () => setIsLoading(false);
      const handleWaiting = () => setIsLoading(true);

      // Set initial loading state based on readyState (0: HAVE_NOTHING, 1: HAVE_METADATA, 2: HAVE_CURRENT_DATA)
      setIsLoading(audio.readyState < 2);

      const cleanup = attachAudioEvents(audio, timeline, {
        handleCanPlay,
        handleWaiting,
        updateTimelineProgress,
        handleAudioEnded,
        handleSeekChange,
      });
      return cleanup;
    }
    return undefined;
  }, []);

  const iconSizeClass = 'md700:w-5 md700:h-5 h-[25px] w-[25px]';
  const ICON = {
    play: <IconPlayerPlayFilled className={iconSizeClass} onClick={handlePlayPause} />,
    pause: <IconPlayerPauseFilled className={iconSizeClass} onClick={handlePlayPause} />,
    sound: <IconVolume className={iconSizeClass} onClick={handleToggleMute} />,
    mute: <IconVolumeOff className={iconSizeClass} onClick={handleToggleMute} />,
    microphoneOff: <IconMicrophoneOff className={`${iconSizeClass} bg-gray-500`} />,
  };

  return (
    <div className={`${isSilent ? 'bg-gray-300' : ''}  audio-player`}>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio ref={audioRef} src={url} />
      <div
        className={`flex items-center controls ${isLoading || isSilent ? 'pointer-events-none opacity-70' : ''}`}
      >
        <button
          type="button"
          className={`p-1 mr-3 flex-shrink-0 rounded-full ${isSilent ? 'bg-gray-500' : 'bg-white/50'} overflow-hidden`}
        >
          {isLoading && (
            <IconLoader2 className="md700:w-5 md700:h-5 h-[25px] w-[25px] animate-spin" />
          )}
          {!isLoading && !isSilent && (isPlaying ? ICON.pause : ICON.play)}
          {isSilent && ICON.microphoneOff}
        </button>
        <input
          type="range"
          className={`${isUserMessage ? 'timeline' : 'timeline-2'} ${isSilent ? 'toggle-off' : ''}`}
          max="100"
          ref={timelineRef}
          // eslint-disable-next-line no-restricted-globals
          value={
            Number.isNaN(currentTime) || !audioRef.current?.duration
              ? 0
              : (currentTime / audioRef.current.duration) * 100
          }
          onChange={handleSeekChange}
        />
        <button type="button" className={`ml-3 ${isSilent ? 'pointer-events-none' : ''}`}>
          {isMuted || isSilent ? ICON.mute : ICON.sound}
        </button>
      </div>
    </div>
  );
}

export default AudioPlayer;
